import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

function TestProcedureDetail() {
  const { id } = useParams();
  const [testProcedure, setTestProcedure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Format text with markdown-style formatting
  const formatText = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((paragraph, index) => {
      if (!paragraph.trim()) return <br key={index} />;
      
      // Check for headings
      if (paragraph.trim().startsWith('#')) {
        const match = paragraph.match(/^(#{1,3})\s+(.+)$/);
        if (match) {
          const level = match[1].length;
          const content = match[2];
          const HeadingTag = `h${level + 2}`;
          return (
            <HeadingTag key={index} className="mb-3" style={{ fontWeight: 'bold', fontSize: level === 1 ? '1.5rem' : level === 2 ? '1.3rem' : '1.1rem' }}>
              {formatInlineText(content)}
            </HeadingTag>
          );
        }
      }
      
      return (
        <p key={index} className="mb-3">
          {formatInlineText(paragraph)}
        </p>
      );
    });
  };

  // Format inline text (bold, etc.)
  const formatInlineText = (text) => {
    const parts = [];
    let lastIndex = 0;
    const regex = /\*\*(.+?)\*\*/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add bold text
      parts.push(<strong key={match.index}>{match[1]}</strong>);
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  useEffect(() => {
    fetchTestProcedure();
    setActiveTab('overview');
  }, [id]);

  const fetchTestProcedure = async () => {
    try {
      const response = await axios.get(`/api/tests-procedures/${id}`);
      setTestProcedure(response.data);
    } catch (error) {
      console.error('Error fetching test/procedure:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!testProcedure) {
    return (
      <div className="container py-5">
        <h1>Test/Procedure Not Found</h1>
        <Link to="/tests-procedures">Back to Tests & Procedures</Link>
      </div>
    );
  }

  // Build tabs array - show all tabs that have content
  const tabs = testProcedure ? (() => {
    const tabList = [];
    
    if (testProcedure.overview || testProcedure.description) {
      tabList.push({ id: 'overview', label: 'Overview', content: testProcedure.overview || testProcedure.description });
    }
    
    if (testProcedure.why_its_done) {
      tabList.push({ id: 'why', label: 'Why it\'s done', content: testProcedure.why_its_done });
    }
    
    if (testProcedure.how_you_prepare) {
      tabList.push({ id: 'prepare', label: 'How you prepare', content: testProcedure.how_you_prepare });
    }
    
    if (testProcedure.what_you_can_expect) {
      tabList.push({ id: 'expect', label: 'What you can expect', content: testProcedure.what_you_can_expect });
    }
    
    if (testProcedure.results) {
      tabList.push({ id: 'results', label: 'Results', content: testProcedure.results });
    }
    
    if (testProcedure.risks) {
      tabList.push({ id: 'risks', label: 'Risks', content: testProcedure.risks });
    }
    
    return tabList;
  })() : [];

  return (
    <>
      {/* Hero Section - Dark Blue Background */}
      <section className="py-5" style={{ backgroundColor: '#003d82', color: '#fff' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: '#fff' }}>Home</Link></li>
              <li className="breadcrumb-item"><Link to="/tests-procedures" style={{ color: '#fff' }}>Tests & Procedures</Link></li>
              <li className="breadcrumb-item active" style={{ color: '#fff' }}>{testProcedure.name}</li>
            </ol>
          </nav>

          <h1 className="display-4 mb-4" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold' }}>
            {testProcedure.name}
          </h1>

          {testProcedure.alias && (
            <p className="lead mb-2" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              Also known as: {testProcedure.alias}
            </p>
          )}
        </div>
      </section>

      {/* Content Section - White Background */}
      <section className="py-5" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          {tabs.length > 0 ? (
            <>
              {/* Tabs Navigation */}
              <ul className="nav nav-tabs mb-4" style={{ borderBottom: '2px solid #e0e0e0' }}>
                {tabs.map((tab) => (
                  <li key={tab.id} className="nav-item">
                    <button
                      className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        border: 'none',
                        borderBottom: activeTab === tab.id ? '3px solid #003d82' : '3px solid transparent',
                        color: activeTab === tab.id ? '#003d82' : '#666',
                        fontWeight: activeTab === tab.id ? '600' : '400',
                        backgroundColor: 'transparent',
                        padding: '12px 20px',
                        cursor: 'pointer'
                      }}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Tab Content */}
              <div className="tab-content" style={{ minHeight: '400px' }}>
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    style={{ display: activeTab === tab.id ? 'block' : 'none' }}
                  >
                    <div className="test-procedure-content" style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333', overflow: 'hidden' }}>
                      {/* Image Display - Float left with text wrapping */}
                      {testProcedure.image && (
                        <figure className="test-procedure-image">
                          <img 
                            src={testProcedure.image.startsWith('/uploads/') ? testProcedure.image : `/uploads/${testProcedure.image}`}
                            alt={testProcedure.name}
                            className="img-fluid rounded"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </figure>
                      )}
                      <div className="test-procedure-text" style={{ overflow: 'hidden' }}>
                        {formatText(tab.content)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="test-procedure-content" style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333', overflow: 'hidden' }}>
              {/* Image Display - Float left with text wrapping */}
              {testProcedure.image && (
                <figure className="test-procedure-image">
                  <img 
                    src={testProcedure.image.startsWith('/uploads/') ? testProcedure.image : `/uploads/${testProcedure.image}`}
                    alt={testProcedure.name}
                    className="img-fluid rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </figure>
              )}
              <div className="test-procedure-text" style={{ overflow: 'hidden' }}>
                {formatText(testProcedure.description)}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default TestProcedureDetail;
